/**
 * Timeline Tab Manager
 * Handles tab switching and content management for slideout panel
 */
class TimelineTabManager {
  constructor(slideoutElement) {
    this.slideout = slideoutElement;
    this.currentTab = 'details';
    this.tabs = new Map();
    this.tabContents = new Map();

    this.init();
  }

  init() {
    this.collectTabs();
    this.setupEventListeners();
  }

  collectTabs() {
    const tabButtons = this.slideout.querySelectorAll('.slideout-tab');
    const tabContents = this.slideout.querySelectorAll('.slideout-tab-content');

    tabButtons.forEach(tab => {
      const tabName = tab.getAttribute('data-tab');
      this.tabs.set(tabName, tab);
    });

    tabContents.forEach(content => {
      const tabName = content.id.replace('tab-', '');
      this.tabContents.set(tabName, content);
    });
  }

  setupEventListeners() {
    this.tabs.forEach((tab, tabName) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchTo(tabName);
      });
    });
  }

  switchTo(tabName) {
    if (!this.tabs.has(tabName) || !this.tabContents.has(tabName)) {
      console.warn(`Tab "${tabName}" not found`);
      return;
    }

    // Update active states
    this.tabs.forEach((tab, name) => {
      tab.classList.toggle('active', name === tabName);
    });

    this.tabContents.forEach((content, name) => {
      content.classList.toggle('active', name === tabName);
    });

    // Store current tab
    this.currentTab = tabName;

    // Trigger tab-specific actions
    this.onTabSwitch(tabName);
  }

  onTabSwitch(tabName) {
    switch (tabName) {
      case 'connections':
        this.loadConnectionsData();
        break;
      case 'settings':
        this.loadSettingsData();
        break;
      case 'details':
      default:
        // Details tab is always ready
        break;
    }
  }

  loadConnectionsData() {
    // This will be called by the slideout manager
    if (window.timelineSlideout && window.timelineSlideout.loadConnections) {
      window.timelineSlideout.loadConnections();
    }
  }

  loadSettingsData() {
    // Load any additional settings data if needed
    console.log('Loading settings data...');
  }

  getCurrentTab() {
    return this.currentTab;
  }

  isTabValid(tabName) {
    if (tabName === 'details') {
      return this.validateDetailsTab();
    }

    // Other tabs are always valid for now
    return true;
  }

  validateDetailsTab() {
    // Check required fields in details tab
    const title = document.getElementById('item-title');
    const startDate = document.getElementById('item-start-date');

    return title && title.value.trim() !== '' &&
      startDate && startDate.value !== '';
  }

  showTabError(tabName, message) {
    const tab = this.tabs.get(tabName);
    if (tab) {
      // Add error indicator to tab
      tab.classList.add('tab-error');

      // Show tooltip or notification
      const errorTooltip = document.createElement('div');
      errorTooltip.className = 'tab-error-tooltip';
      errorTooltip.textContent = message;
      tab.appendChild(errorTooltip);

      // Remove error after delay
      setTimeout(() => {
        tab.classList.remove('tab-error');
        if (errorTooltip.parentNode) {
          errorTooltip.parentNode.removeChild(errorTooltip);
        }
      }, 3000);
    }
  }

  clearTabErrors() {
    this.tabs.forEach(tab => {
      tab.classList.remove('tab-error');
      const tooltip = tab.querySelector('.tab-error-tooltip');
      if (tooltip) {
        tooltip.parentNode.removeChild(tooltip);
      }
    });
  }

  disableTab(tabName) {
    const tab = this.tabs.get(tabName);
    if (tab) {
      tab.disabled = true;
      tab.classList.add('disabled');
    }
  }

  enableTab(tabName) {
    const tab = this.tabs.get(tabName);
    if (tab) {
      tab.disabled = false;
      tab.classList.remove('disabled');
    }
  }

  setTabBadge(tabName, count) {
    const tab = this.tabs.get(tabName);
    if (tab) {
      // Remove existing badge
      const existingBadge = tab.querySelector('.tab-badge');
      if (existingBadge) {
        existingBadge.parentNode.removeChild(existingBadge);
      }

      // Add new badge if count > 0
      if (count > 0) {
        const badge = document.createElement('span');
        badge.className = 'tab-badge';
        badge.textContent = count;
        tab.appendChild(badge);
      }
    }
  }
}

window.TimelineTabManager = TimelineTabManager;
